<template>
	<div style="height: 100%">
		<b-row class="justify-content-between pb-2">
			<button
				:disabled="activeIndex === 0"
				type="button"
				class="btn-transparent"
				:class="activeIndex === 0 && !isNotMobile ? 'text-grey-light' : 'text-color-rhapsody-in-blue'"
				@click="previousImage"
			>
				<component :is="getLucideIcon('ChevronLeft')" :size="20" />
			</button>
			<div v-if="getFileExtensionFromXid(imageUrls[activeIndex]) !== 'pdf'" class="d-flex align-items-center justify-content-center" style="gap: 7px">
				<div v-b-tooltip.hover.left.v-light="'Zoom In'">
					<button type="button" class="btn-transparent text-color-rhapsody-in-blue" @click="zoomIn">
						<component :is="getLucideIcon('ZoomIn')" :size="20" />
					</button>
				</div>
				<div v-b-tooltip.hover.right.v-light="'Zoom Out'">
					<button
						type="button"
						:disabled="zoom <= initialZoom"
						class="btn-transparent"
						:class="zoom <= initialZoom && !isNotMobile ? 'text-grey-light' : 'text-color-rhapsody-in-blue'"
						@click="zoomOut"
					>
						<component :is="getLucideIcon('ZoomOut')" :size="20" />
					</button>
				</div>
			</div>
			<button
				:disabled="activeIndex === imageUrls.length - 1"
				type="button"
				class="btn-transparent text-color-rhapsody-in-blue"
				:class="activeIndex === imageUrls.length - 1 && !isNotMobile ? 'text-grey-light' : 'text-color-rhapsody-in-blue'"
				@click="nextImage"
			>
				<component :is="getLucideIcon('ChevronRight')" :size="20" />
			</button>
		</b-row>
		<div class="carousel-container d-flex justify-content-center align-items-center" style="height: 95%; overflow: hidden">
			<div class="carousel-content h-100 w-100">
				<div class="parent h-100" @mousedown="startDrag" @touchstart="startDrag">
					<div
						v-if="getFileExtensionFromXid(imageUrls[activeIndex]) !== 'pdf'"
						class="slide"
						style="height: 100%; overflow: auto"
						:style="preventWebTouch"
					>
						<b-img-lazy
							ref="zoomableImage"
							:src="getImage(imageUrls[activeIndex])"
							:alt="imageUrls[activeIndex]"
							fluid-grow
							block
							style="object-fit: contain; width: 100%; height: 100%; border: 1px solid #d9d9d9"
							:style="[zoomStyle, containerStyle]"
						/>
					</div>
					<div v-else class="iframe-pdf h-100" style="justify-content: space-between">
						<iframe v-if="!maxMobileScreenWidth" :src="getImage(imageUrls[activeIndex])" style="width: 100%; height: 100%" frameborder="0" />
						<PdfViewer
							v-else
							:ref="`slidePdf_${activeIndex}`"
							:is-pwa-ios="isPwaIOS"
							:src="getImage(imageUrls[activeIndex])"
							:xid="imageUrls[activeIndex]"
							:manager="manager"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import _ from 'lodash';
import { getFileExtension } from '@/shared/helpers';
import globalMixin from '@/mixins/global.mixin';
import invoiceToolsMixin from '@/mixins/invoicesTools.mixin';
import PdfViewer from '@/components/Packages/FIlesPreviewer/components/FileViewers/PdfViewer';
import ImageShowRoomManager from '@/components/Packages/FIlesPreviewer/imageShowroom.manager';

export default {
	name: 'InvoiceCarousel',
	components: {
		PdfViewer
	},
	mixins: [globalMixin, invoiceToolsMixin],
	props: {
		imageUrls: {
			type: Array,
			default: () => []
		},
		activeIndex: {
			type: Number,
			default: 0
		}
	},
	data() {
		return {
			manager: new ImageShowRoomManager(this, {}),
			defaultZoomScale: 0.3,
			initialZoom: 1,
			zoom: 1,
			isDragging: false,
			startX: 0,
			startY: 0,
			offsetX: 0,
			offsetY: 0
		};
	},
	computed: {
		isPwaIOS() {
			return this.$isPwa() && this.$isIOSDevice();
		},
		isNotMobile() {
			return !this.$isPwa() && !this.$isResponsive();
		},
		preventWebTouch() {
			if (this.isNotMobile) {
				return {
					touchAction: 'none',
					userSelect: 'none'
				};
			} else return '';
		},
		zoomStyle() {
			return {
				transform: `scale(${this.zoom})`,
				transformOrigin: 'top left',
				transition: this.isDragging ? 'none' : 'transform 0.2s ease'
			};
		},
		cursorAspect() {
			if (this.zoom <= this.initialZoom) {
				return 'auto';
			} else if (this.isDragging) {
				return 'grabbing';
			} else return 'grab';
		},
		containerStyle() {
			return {
				cursor: this.cursorAspect,
				position: 'relative',
				left: `${this.offsetX}px`,
				top: `${this.offsetY}px`
			};
		}
	},
	mounted() {
		this.zoom = this.initialZoom;
		this.reinitializeDragPosition();

		// Add global event listeners
		document.addEventListener('mousemove', this.onDrag);
		document.addEventListener('mouseup', this.stopDrag);
		document.addEventListener('touchmove', this.onDrag);
		document.addEventListener('touchend', this.stopDrag);

		// Cleanup listeners on destroy
		this.$once('hook:destroyed', () => {
			document.removeEventListener('mousemove', this.onDrag);
			document.removeEventListener('mouseup', this.stopDrag);
			document.removeEventListener('touchmove', this.onDrag);
			document.removeEventListener('touchend', this.stopDrag);
		});
	},
	methods: {
		getFileExtensionFromXid(docXid) {
			if (_.isNil(docXid)) return;

			return getFileExtension(docXid);
		},
		getImage(picture) {
			return process.env.VUE_APP_GQL + '/images/' + picture;
		},
		previousImage() {
			this.zoom = this.initialZoom;
			this.reinitializeDragPosition();
			this.$emit('previousImage');
		},
		nextImage() {
			this.zoom = this.initialZoom;
			this.reinitializeDragPosition();
			this.$emit('nextImage');
		},
		zoomIn() {
			this.zoom += this.defaultZoomScale;
		},
		zoomOut() {
			this.zoom = Math.max(this.initialZoom, this.zoom - this.defaultZoomScale);
			if (this.zoom <= this.initialZoom) {
				this.reinitializeDragPosition();
			}
		},
		reinitializeDragPosition() {
			(this.startX = 0), (this.startY = 0), (this.offsetX = 0), (this.offsetY = 0);
		},
		getEventCoords(e) {
			if (e.touches) {
				return { x: e.touches[0].clientX, y: e.touches[0].clientY };
			} else {
				return { x: e.clientX, y: e.clientY };
			}
		},
		startDrag(e) {
			if (this.isNotMobile) e.preventDefault();
			if (this.zoom > this.initialZoom) {
				const { x, y } = this.getEventCoords(e);
				this.isDragging = true;
				this.startX = x - this.offsetX;
				this.startY = y - this.offsetY;
			}
		},
		onDrag(e) {
			if (!this.isDragging) return;
			const { x, y } = this.getEventCoords(e);
			this.offsetX = x - this.startX;
			this.offsetY = y - this.startY;
		},
		stopDrag() {
			this.isDragging = false;
		}
	}
};
</script>
<style scoped>
.text-grey-light {
	color: oklch(70.7% 0.022 261.325);
}
</style>
